import React, { useEffect, useState } from 'react';
import { collection, query, where, orderBy, limit, onSnapshot, addDoc, doc, updateDoc, deleteDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { getToken, onMessage } from 'firebase/messaging';
import { logEvent } from 'firebase/analytics';
import { db, functions, messaging, analytics } from '../firebase';
import useGardenData from '../hooks/useGardenData';

import {
    Button,
    Container,
    Box,
    Typography,
    Stack,
    MenuItem,
    IconButton,
    Menu,
    ListItemIcon,
    ListItemText,
    LinearProgress,
} from '@mui/material';
import { Refresh, MoreVert } from '@mui/icons-material';
import DailyGardenSchedule from './dashboard/DailyGardenSchedule';
import TaskFormModal, { TaskFormData } from './shared/TaskFormModal';
import TaskCompletionDialog from './shared/TaskCompletionDialog';
import ConfirmationDialog from './shared/ConfirmationDialog';
import { Task, WeatherDay, TaskPayload } from '../types/shared';
import { useTranslation } from 'react-i18next';
import { LoadingScreen } from './shared/Loading';
import { getLocalDateString } from '../utils/dateUtils';

interface LocationWeather {
    date: string;
    minTemp: number;
    maxTemp: number;
    precipitation: number;
    weatherCode: number;
    description?: string;
}

const Tasks: React.FC = () => {
    const { t } = useTranslation();
    const { user, plants, spaces, userProfile, loading: dataLoading } = useGardenData();
    const [tasks, setTasks] = useState<Task[]>([]);
    const [weather, setWeather] = useState<LocationWeather[] | null>(null);
    const [loadingTasks, setLoadingTasks] = useState(false);

    // Task Form State
    const [showTaskForm, setShowTaskForm] = useState(false);
    const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
    const [editingTask, setEditingTask] = useState<Task | null>(null);
    const [deleteConfirmation, setDeleteConfirmation] = useState<{ open: boolean; taskId: string | null }>({
        open: false,
        taskId: null
    });

    // Task Completion Dialog State
    const [completionTask, setCompletionTask] = useState<Task | null>(null);
    const [completionSubmitting, setCompletionSubmitting] = useState(false);

    const setupNotifications = async (userId: string) => {
        try {
            const permission = await Notification.requestPermission();
            if (permission === 'granted') {
                const token = await getToken(messaging, {
                    vapidKey: 'BFmXXbmXcM7RB4xPb8FDwtg5yRceACNWgcS27nYMYTx73Cd2HQEwP3u-mj8rBLfAdaIsH8tvlVcpEpIqUVrq-c0'
                });
                if (token) {
                    await updateDoc(doc(db, 'users', userId), {
                        fcmToken: token,
                        notificationsEnabled: true,
                        updatedAt: new Date()
                    });
                }
            }
        } catch (error) {
            console.error('Error setting up notifications:', error);
        }

        onMessage(messaging, (payload) => {
            if (Notification.permission === 'granted' && payload.notification) {
                new Notification(payload.notification.title || 'New Notification', {
                    body: payload.notification.body,
                    icon: '/logo192.png'
                });
            } else if (payload.notification) {
                alert(`${payload.notification.title}\n\n${payload.notification.body}`);
            }
        });
    };

    useEffect(() => {
        if (user) {
            setupNotifications(user.uid);
        }
    }, [user]);

    // Weather Subscription
    useEffect(() => {
        if (!userProfile?.location) return;

        const today = getLocalDateString();
        const locationId = userProfile.location.toLowerCase().replace(/\s+/g, '_');
        const q = query(
            collection(db, 'weather', locationId, 'dates'),
            where('date', '>=', today),
            orderBy('date'),
            limit(10)
        );

        const unsubscribe = onSnapshot(q, (snapshot) => {
            setWeather(snapshot.docs.map(doc => doc.data() as LocationWeather));
        }, (error) => {
            console.error('Failed to subscribe to weather:', error);
        });

        return () => unsubscribe();
    }, [userProfile?.location]);

    // Tasks Subscription
    useEffect(() => {
        if (!user) return;
        const unsubscribe = onSnapshot(
            query(collection(db, 'tasks'), where('userId', '==', user.uid), where('status', '==', 'pending'), orderBy('date'), orderBy('priority')),
            (snapshot) => {
                const newTasks = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Task));
                setTasks(newTasks);

                // Update app badge
                if ('setAppBadge' in navigator && 'clearAppBadge' in navigator) {
                    const today = getLocalDateString();

                    // Strict equality to match "tasks for today ONLY"
                    const pendingTodayCount = newTasks.filter(t => t.date === today).length;

                    if (pendingTodayCount > 0) {
                        (navigator as any).setAppBadge(pendingTodayCount).catch((e: any) => {
                            console.error('Error setting app badge:', e);
                        });
                    } else {
                        (navigator as any).clearAppBadge().catch((e: any) => {
                            console.error('Error clearing app badge:', e);
                        });
                    }
                }
            }
        );
        return () => unsubscribe();
    }, [user]);

    const handleGetTasks = async (forceRegenerate = false) => {
        setLoadingTasks(true);
        try {
            const generateTasks = httpsCallable<{ forceRegenerate: boolean }, { tasks: Task[] }>(functions, 'generateGardenTasks');
            const result = await generateTasks({ forceRegenerate });
            if (result.data.tasks) {
                setTasks(result.data.tasks);
            }
        } catch (error) {
            console.error('Failed to generate tasks:', error);
        } finally {
            setLoadingTasks(false);
        }
    };

    const handleUpdateTaskStatus = async (taskId: string, status: 'completed' | 'skipped' | 'cancelled' | 'pending', completionPayload?: Partial<TaskPayload>) => {
        const task = tasks.find(t => t.id === taskId);
        const taskType = task ? task.type : 'generic';

        // Optimistic update — reflect change immediately before awaiting the cloud function
        const previousTasks = tasks;
        setTasks(prev =>
            prev.map((rec: Task) =>
                rec.id === taskId
                    ? { ...rec, status, completedAt: status === 'completed' ? new Date() : undefined }
                    : rec
            )
        );

        try {
            const updateStatus = httpsCallable<{ taskId: string; status: string; completionPayload?: Partial<TaskPayload> }, void>(functions, 'updateTaskStatus');
            await updateStatus({ taskId, status, ...(completionPayload ? { completionPayload } : {}) });
            if (status === 'completed') {
                logEvent(analytics, 'task_completed', { task_id: taskId, type: taskType });
            }
        } catch (error) {
            console.error('Failed to update status:', error);
            // Rollback on failure
            setTasks(previousTasks);
        }
    };

    const handleCompleteWithPayload = (task: Task) => {
        setCompletionTask(task);
    };

    const handleCompletionSubmit = async (taskId: string, payload: Partial<TaskPayload>) => {
        setCompletionSubmitting(true);
        try {
            await handleUpdateTaskStatus(taskId, 'completed', payload);
        } finally {
            setCompletionSubmitting(false);
            setCompletionTask(null);
        }
    };

    const handleTaskSubmit = async (formData: TaskFormData) => {
        if (!user) return;

        if (editingTask) {
            // Update existing task
            await updateDoc(doc(db, 'tasks', editingTask.id), {
                ...formData,
                updatedAt: new Date()
            });
        } else {
            // Create new task
            const newTask = {
                ...formData,
                type: 'GENERIC',
                payload: {
                    plantIds: formData.plantIds || [],
                },
                userId: user.uid,
                status: 'pending',
                createdAt: new Date(),
                updatedAt: new Date(),
                isManual: true,
                reason: 'Manual entry',
                date: formData.date
            };
            await addDoc(collection(db, 'tasks'), newTask);
        }
        setShowTaskForm(false);
        setEditingTask(null);
    };

    const handleEditTask = (task: Task) => {
        setEditingTask(task);
        setShowTaskForm(true);
    };

    const handleDeleteTaskClick = (taskId: string) => {
        setDeleteConfirmation({ open: true, taskId });
    };

    const handleConfirmDelete = async () => {
        if (!deleteConfirmation.taskId) return;
        try {
            await deleteDoc(doc(db, 'tasks', deleteConfirmation.taskId));
        } catch (error) {
            console.error('Error deleting task:', error);
            alert(t('tasks.error_delete'));
        } finally {
            setDeleteConfirmation({ open: false, taskId: null });
        }
    };

    // Map LocationWeather to WeatherDay for DailyGardenSchedule
    const weatherDays: WeatherDay[] = weather ? weather.map(w => ({
        date: w.date,
        minTemp: w.minTemp,
        maxTemp: w.maxTemp,
        precipitation: w.precipitation,
        weatherCode: w.weatherCode,
        description: w.description || ''
    })) : [];

    if (dataLoading) return <LoadingScreen />;

    return (
        <Box sx={{ flexGrow: 1, pb: 4 }}>
            <Container maxWidth="md" sx={{ mt: 3 }}>
                <Stack spacing={3}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="h5" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                            {t('tasks.garden_schedule')}
                        </Typography>
                        <Stack direction="row" spacing={1} alignItems="center">
                            <Button
                                variant="contained"
                                onClick={() => setShowTaskForm(true)}
                                size="small"
                                sx={{ borderRadius: 2 }}
                            >
                                {t('tasks.add_task')}
                            </Button>
                            <IconButton
                                size="small"
                                onClick={(e) => setMenuAnchorEl(e.currentTarget)}
                            >
                                <MoreVert />
                            </IconButton>
                            <Menu
                                anchorEl={menuAnchorEl}
                                open={Boolean(menuAnchorEl)}
                                onClose={() => setMenuAnchorEl(null)}
                            >
                                <MenuItem
                                    onClick={() => { setMenuAnchorEl(null); handleGetTasks(true); }}
                                    disabled={loadingTasks}
                                >
                                    <ListItemIcon><Refresh fontSize="small" /></ListItemIcon>
                                    <ListItemText>{t('tasks.regenerate_tasks')}</ListItemText>
                                </MenuItem>
                            </Menu>
                        </Stack>
                    </Box>

                    {loadingTasks && (
                        <LinearProgress sx={{ borderRadius: 1 }} />
                    )}

                    {weatherDays.length > 0 || tasks.length > 0 ? (
                        <DailyGardenSchedule
                            weatherDays={weatherDays}
                            tasks={tasks}
                            plants={plants}
                            spaces={spaces}
                            onUpdateStatus={handleUpdateTaskStatus}
                            onCompleteWithPayload={handleCompleteWithPayload}
                            onEditTask={handleEditTask}
                            onDeleteTask={handleDeleteTaskClick}
                            location={userProfile?.location || ''}
                        />
                    ) : (
                        <Box sx={{ textAlign: 'center', py: 8 }}>
                            <Typography color="text.secondary">{t('tasks.no_data')}</Typography>
                            <Button onClick={() => handleGetTasks(true)} sx={{ mt: 2 }}>{t('tasks.init_schedule')}</Button>
                        </Box>
                    )}
                </Stack>
            </Container>

            {/* Manual Task Dialog */}
            <TaskFormModal
                open={showTaskForm}
                onClose={() => { setShowTaskForm(false); setEditingTask(null); }}
                onSubmit={handleTaskSubmit}
                initialData={editingTask}
                title={editingTask ? t('tasks.edit_task') : t('tasks.add_manual_task')}
            />

            {/* Delete Confirmation Dialog */}
            <ConfirmationDialog
                open={deleteConfirmation.open}
                title={t('tasks.delete_task_title')}
                message={t('tasks.delete_task_message')}
                confirmLabel={t('tasks.delete')}
                onConfirm={handleConfirmDelete}
                onCancel={() => setDeleteConfirmation({ open: false, taskId: null })}
                severity="error"
            />

            {/* Task Completion Dialog */}
            <TaskCompletionDialog
                open={completionTask !== null}
                task={completionTask}
                spaces={spaces}
                onClose={() => setCompletionTask(null)}
                onComplete={handleCompletionSubmit}
                isSubmitting={completionSubmitting}
            />
        </Box>
    );
};

export default Tasks;
